import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../../components/Head/SeoTags";
import { BookmarkBoardMock } from "../../../components/Mockups/BookmarkBoardMock";
import { RelatedGuide } from "../../../components/RelatedGuide/RelatedGuide";

const MultiCollection = () => {
  const { t } = useTranslation();

  return (
    <>
      <SeoTags
        title={t("featurePages.multiCollection.seo.title")}
        description={t("featurePages.multiCollection.seo.description")}
        keywords={t("featurePages.multiCollection.seo.keywords")}
      />

      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t("featurePages.multiCollection.seo.title"),
            description: t("featurePages.multiCollection.seo.description"),
            url: "https://onbookmarks.com/features/multi-collection/",
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              applicationCategory: "ProductivityApplication",
              operatingSystem: "Web, iOS, Android",
              featureList: [
                "Multi-collection bookmark assignment",
                "No duplicate bookmarks needed",
                "Flexible organization system",
                "Cross-reference bookmarks",
                "Find bookmarks in any context"
              ],
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
                description: "Free plan includes multi-collection"
              }
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://onbookmarks.com"
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Features",
                  item: "https://onbookmarks.com/features/"
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Multi-Collection",
                  item: "https://onbookmarks.com/features/multi-collection/"
                }
              ]
            }
          }),
        }}
      />

      <section className="hero bg-purple-subtle">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-6">
              <div className="d-flex flex-column gap-4">
                <div>
                  <span className="badge bg-purple text-white px-3 py-2">
                    {t("home.multiCollection.badge")}
                  </span>
                </div>
                <h1 className="display-4 fw-bold">
                  {t("featurePages.multiCollection.hero.title")}
                </h1>
                <p className="lead">
                  {t("featurePages.multiCollection.hero.description")}
                </p>
                <div>
                  <Link
                    to={`${process.env.REACT_APP_APP_URL}/register`}
                    className="btn btn-primary btn-lg px-4"
                  >
                    {t("featurePages.multiCollection.hero.cta")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <BookmarkBoardMock />
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.multiCollection.problem.title")}
              </h2>
              <p className="lead text-muted">
                {t("featurePages.multiCollection.problem.description")}
              </p>
            </div>
          </div>

          <div className="row g-4 mb-5">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-folder-x fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.multiCollection.problem.single.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.multiCollection.problem.single.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-files fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.multiCollection.problem.duplicates.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.multiCollection.problem.duplicates.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-search-heart fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.multiCollection.problem.lost.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.multiCollection.problem.lost.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div className="mb-3">
                <span className="badge bg-purple text-white px-3 py-2">
                  {t("featurePages.multiCollection.solution.badge")}
                </span>
              </div>
              <BookmarkBoardMock />
            </div>
            <div className="col-lg-6">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.multiCollection.solution.title")}
              </h2>
              <p className="lead mb-4">
                {t("featurePages.multiCollection.solution.description")}
              </p>
              <ul className="list-unstyled">
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.multiCollection.solution.benefit1.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.multiCollection.solution.benefit1.description")}
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.multiCollection.solution.benefit2.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.multiCollection.solution.benefit2.description")}
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.multiCollection.solution.benefit3.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.multiCollection.solution.benefit3.description")}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold mb-4">
              {t("featurePages.multiCollection.howItWorks.title")}
            </h2>
            <p className="lead text-muted">
              {t("featurePages.multiCollection.howItWorks.description")}
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">1</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.multiCollection.howItWorks.step1.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.multiCollection.howItWorks.step1.description")}
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">2</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.multiCollection.howItWorks.step2.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.multiCollection.howItWorks.step2.description")}
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">3</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.multiCollection.howItWorks.step3.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.multiCollection.howItWorks.step3.description")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.multiCollection.useCases.title")}
              </h2>
              <p className="lead text-muted mb-5">
                {t("featurePages.multiCollection.useCases.description")}
              </p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-code-slash fs-3 text-purple me-3"></i>
                    <h3 className="h5 mb-0">
                      {t("featurePages.multiCollection.useCases.developer.title")}
                    </h3>
                  </div>
                  <p className="text-muted mb-3">
                    {t("featurePages.multiCollection.useCases.developer.description")}
                  </p>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.multiCollection.useCases.developer.example1")}
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.multiCollection.useCases.developer.example2")}
                    </li>
                    <li>
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.multiCollection.useCases.developer.example3")}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-book fs-3 text-purple me-3"></i>
                    <h3 className="h5 mb-0">
                      {t("featurePages.multiCollection.useCases.researcher.title")}
                    </h3>
                  </div>
                  <p className="text-muted mb-3">
                    {t("featurePages.multiCollection.useCases.researcher.description")}
                  </p>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.multiCollection.useCases.researcher.example1")}
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.multiCollection.useCases.researcher.example2")}
                    </li>
                    <li>
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.multiCollection.useCases.researcher.example3")}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RelatedGuide
        to="/blog/how-to-organize-bookmarks-into-collections"
        title="How to Organize Bookmarks into Collections"
        description="A collections-and-tags system that scales past a few hundred bookmarks."
      />

      <section className="py-5">
        <div className="container">
          <div className="bg-purple rounded-4 p-5 text-center text-white">
            <h2 className="h1 fw-bold mb-4">
              {t("featurePages.multiCollection.cta.title")}
            </h2>
            <p className="lead mb-4">
              {t("featurePages.multiCollection.cta.description")}
            </p>
            <Link
              to={`${process.env.REACT_APP_APP_URL}/register`}
              className="btn btn-light btn-lg px-5"
            >
              {t("featurePages.multiCollection.cta.button")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default MultiCollection;